import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { IconButton } from "@evertrue/et-components";
import { colors } from "style/colors";

// Stopgap: public/index.html adds `icons-unavailable` to <html> when an icon
// font script fails to load. Remove with that onerror handler once fonts are stable.
export const IconFallbackLabel = styled.span`
  display: none;
  margin-left: 4px;
  font-size: 14px;
  color: ${colors.linkText};
  cursor: pointer;

  .icons-unavailable & {
    display: inline;
  }
`;

const hasFlag = () =>
  typeof document !== "undefined" &&
  document.documentElement.classList.contains("icons-unavailable");

export const useIconsUnavailable = () => {
  const [unavailable, setUnavailable] = useState(hasFlag);

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => setUnavailable(hasFlag()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    setUnavailable(hasFlag());
    return () => observer.disconnect();
  }, []);

  return unavailable;
};

const Wrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  min-width: 24px;
  min-height: 24px;
  cursor: pointer;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.06);
  }
`;

export const SafeIconButton = ({ title, onClick, className, ...rest }) => (
  <Wrapper className={className}>
    <StyledIconButton title={title} onClick={onClick} {...rest} />
    {title && (
      <IconFallbackLabel aria-hidden="true" onClick={onClick}>
        {title}
      </IconFallbackLabel>
    )}
  </Wrapper>
);

SafeIconButton.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
};
